'use client'
import type { ProductSimpleListProps } from '@maestro/commerce-blocks/components/product-simple-list/product-simple-list.types'

import { useTranslations } from 'next-intl'

import { ProductCard } from '@maestro/commerce-blocks/components/product-card'
import { useProductSimpleList } from '@maestro/commerce-blocks/components/product-simple-list/baton/use-product-simple-list'
import { useManyProductsPrice } from '@maestro/commerce-blocks/hooks/use-many-products-price'
import { useManyProductsStock } from '@maestro/commerce-blocks/hooks/use-many-products-stock'
import { TriggeredFrom } from '@maestro/data-layer'
import { theme } from '@maestro/local/theme'

import '@maestro/commerce-blocks/components/product-simple-list/styles/product-simple-list.scss'

export default function ProductSimpleList({
  products: productsProps,
  imageConfig = 'productList',
  displayStockStatus,
  enableClientSideStockFetching,
  displayPrice,
  enableClientSidePriceFetching,
  showWishlistButton = false,
  addToCartType = theme().product.addToCartDisplayType.productList,
  addToCartTitle,
  addToCartNotificationType = 'minimal',
  eventListProps,
  keyPrefix = 'product-simple-list',
  recommendationProps,
  productsTitleTag = 'h3',
  renderProductAction,
}: ProductSimpleListProps) {
  const t = useTranslations('commerce-blocks')
  const { products: productsWithStock, isLoading: isStockLoading } =
    useManyProductsStock(
      productsProps,
      displayStockStatus && enableClientSideStockFetching,
      eventListProps?.listType
    )
  const { products } = useManyProductsPrice({
    serverProducts: productsWithStock,
    fetchPriceClientSide: displayPrice && enableClientSidePriceFetching,
    listingType: eventListProps?.listType,
  })
  const { containerRef } = useProductSimpleList({ products, eventListProps })
  return (
    <ul className="product-simple-list" ref={containerRef}>
      {products.map((product, idx) => (
        <li
          className="product-simple-list__item"
          key={`${keyPrefix}-${product.id}`}
          data-product-id={product.id}
        >
          <ProductCard
            product={product}
            imageConfig={imageConfig}
            isCompact
            addToCartType={addToCartType}
            addToCartTitle={addToCartTitle || t('_globals.add')}
            addToCartNotificationType={addToCartNotificationType}
            displayStockStatus={displayStockStatus}
            displayPrice={displayPrice}
            isStockLoading={isStockLoading}
            showWishlistButton={showWishlistButton}
            titleTag={productsTitleTag}
            eventProps={{
              productIndex: idx,
              eventListProps,
              triggeredFrom: TriggeredFrom.PLP,
            }}
            recommendationProps={recommendationProps}
            customButton={renderProductAction?.(product, isStockLoading)}
          />
        </li>
      ))}
    </ul>
  )
}
